import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { TokenService } from "../../services/tokenService";

// Global flag to prevent multiple handleRedirectPromise calls
let redirectHandled = false;

/**
 * AuthHandler component to handle redirect responses from Azure AD
 * This component should be included in your main app to ensure
 * redirect responses are properly processed on mobile devices
 */
const AuthHandler: React.FC = () => {
  const { instance } = useMsal();

  useEffect(() => {
    // Handle redirect promise - should only be called ONCE per app session
    const handleRedirect = async () => {
      if (redirectHandled) {
        return;
      }
      try {
        const response = await instance.handleRedirectPromise();

        // Mark that we've handled the redirect regardless of response
        redirectHandled = true;

        if (response) {
          console.log("AuthNavigationHandler: Redirect response received, clearing stale tokens");
          
          // IMPORTANT: Clear any existing backend token when returning from Azure AD login
          // This forces a fresh token exchange and prevents using stale/expired tokens
          const existingToken = localStorage.getItem("bestekJwtToken");
          if (existingToken) {
            const isExpired = TokenService.isTokenExpired(existingToken);
            if (isExpired) {
              console.log("AuthNavigationHandler: Removing expired backend token");
              localStorage.removeItem("bestekJwtToken");
            }
          }
          
          // Clear the auth method and login progress flags since authentication is complete
          sessionStorage.removeItem("auth-method");
          sessionStorage.removeItem("login-in-progress");
          sessionStorage.removeItem("login-start-time");

          // Add a flag to indicate successful authentication
          sessionStorage.setItem("auth-completed", "true");
          sessionStorage.setItem("auth-completed-time", Date.now().toString());

          // The useAuth hook will handle the backend token exchange
          // when it detects that isAuthenticated has become true
        } else {
          // No redirect response - this is normal for initial page loads
        }
      } catch (error) {
        console.error(
          "AuthNavigationHandler: Error handling redirect promise:",
          error
        );

        // If it's an interaction_in_progress error, don't mark as handled yet
        if (
          error instanceof Error &&
          error.message.includes("interaction_in_progress")
        ) {
          console.log(
            "AuthNavigationHandler: Interaction in progress, will retry later"
          );
          redirectHandled = false; // Allow retry
        }
      }
    };

    handleRedirect();
  }, [instance]); // Only depend on instance, not location changes

  // This component doesn't render anything
  return null;
};

export default AuthHandler;
